import { useEffect, useSyncExternalStore } from 'react';
import { getTelegramWebApp } from '@/hooks/useTelegram';
import { CHROME_TOP, clipToAppChrome, getVisibleArea } from '@/utils/viewportBand';
import type { VerticalBand } from '@/utils/viewportBand';

/**
 * Virtual-keyboard handling for the Telegram Mini App on iOS/Android.
 *
 * Regular browsers (mobile Safari, Chrome, narrow desktop windows) are left
 * alone — they handle the keyboard natively.
 *
 * Inside Telegram mobile the layout never changes on focus (Header, BottomNav
 * and the tab strip stay); the hook only scrolls:
 * - While a text field is focused `useKeyboardFieldFocused()` is true —
 *   `AppLayout` pins the Headroom header with it, so the hook's own scrolling
 *   never slides the header away (nor does it come back on the next hop).
 * - The field's nearest scroll container gets a bottom spacer
 *   (`.keyboard-spacer::after`, height `--keyboard-spacer`) equal to the part
 *   of that container hidden behind the keyboard, so the last fields can
 *   scroll above it. Measured from geometry, so it is 0 when the webview
 *   itself shrank (no double counting). A container that grows with its
 *   content (no height bound — the document scrolls instead) is skipped.
 * - Once the viewport settles, any keyboard pan of a viewport-tall document is
 *   undone and the field is centred (slightly above the middle) in the visible
 *   band: from the visible header (`[data-app-chrome="top"]`) or the top of its
 *   scroll container down to the keyboard / visible BottomNav
 *   (`[data-app-chrome="bottom"]`). In a stacked form the previous field stays
 *   visible above and the next one below, so field → field taps work with the
 *   keyboard up. Fields with results under them — `type="search"` and
 *   searchable selects (`role="combobox"`) — are pinned 8 px under the band top
 *   instead, so the results / menu get the whole band. One instant scroll,
 *   clamped by the containers.
 * - A later viewport change for the same field only re-centres when the band
 *   height changed noticeably (keyboard came up late); otherwise it just keeps
 *   the field visible, so the user's own scrolling is never fought.
 * - A field inside the header (`[data-app-chrome="top"]`, header search) is
 *   always visible: no spacer and no scrolling — only the iOS document pan is
 *   undone, so the page under it never moves.
 * Container heights are never set. Blur drops the spacer; scroll positions
 * stay where the user left them.
 *
 * Only real text inputs count: checkboxes, toggles, buttons, readOnly inputs
 * (ScoreSpinner) and `inputmode="none"` / `aria-readonly` inputs (react-select's
 * non-searchable DummyInput) never open a keyboard and are ignored.
 */

const SPACER_CLASS = 'keyboard-spacer';
const SPACER_VAR = '--keyboard-spacer';

/** Viewport quiet time before aligning (keyboard animation emits a burst of events). */
const SETTLE_DELAY = 120;
/** Align even when the viewport reports nothing (keyboard already up or no events). */
const FALLBACK_DELAY = 400;
/** Focus moving field → field keeps the spacer and the band. */
const CLOSE_DELAY = 300;
/** Where the field's centre goes, as a share of the band height from its top. */
const CENTER_RATIO = 0.45;
/** Gap between a pinned search field / combobox and the band top. */
const PIN_GAP = 8;
/** Breathing room kept around a field that is taller than the band allows. */
const CLEARANCE = 24;
/** Band height change (keyboard came up / resized) that warrants a re-centre. */
const BAND_CHANGE = 60;
/** Ignore sub-threshold corrections — no 1–3 px twitches. */
const MIN_SCROLL = 4;

const NON_TYPEABLE_TYPES = new Set([
  'checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image', 'hidden',
]);

// "A text field is focused" for components (AppLayout pins the header with it).
let fieldFocused = false;
const fieldFocusListeners = new Set<() => void>();

const setFieldFocused = (value: boolean) => {
  if (value === fieldFocused) return;
  fieldFocused = value;
  fieldFocusListeners.forEach((listener) => listener());
};

const subscribeFieldFocus = (listener: () => void) => {
  fieldFocusListeners.add(listener);
  return () => {
    fieldFocusListeners.delete(listener);
  };
};

/** True while a keyboard-opening field is focused inside Telegram mobile. */
export const useKeyboardFieldFocused = (): boolean =>
  useSyncExternalStore(subscribeFieldFocus, () => fieldFocused);

const isTelegramMobile = (): boolean => {
  const tg = getTelegramWebApp();
  return !!tg && (tg.platform === 'ios' || tg.platform === 'android');
};

/** A focused element that brings up the virtual keyboard. */
const isEditableInput = (el: Element | null): el is HTMLInputElement | HTMLTextAreaElement => {
  if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return false;
  if (el.readOnly || el.disabled) return false;
  if (el.inputMode === 'none' || el.getAttribute('aria-readonly') === 'true') return false;
  return !(el instanceof HTMLInputElement && NON_TYPEABLE_TYPES.has(el.type.toLowerCase()));
};

/** Search fields and comboboxes show results under them — pin them to the top. */
const isPinnedField = (el: HTMLElement): boolean =>
  (el instanceof HTMLInputElement && el.type === 'search') || el.getAttribute('role') === 'combobox';

const isScrollContainer = (el: Element): boolean => {
  const { overflowY } = getComputedStyle(el);
  return overflowY === 'auto' || overflowY === 'scroll';
};

/** Scroll containers from the field outwards, ending with the document. */
const getScrollChain = (el: HTMLElement): HTMLElement[] => {
  const root = document.scrollingElement as HTMLElement;
  const chain: HTMLElement[] = [];
  for (let node = el.parentElement; node && node !== document.body && node !== root; node = node.parentElement) {
    if (isScrollContainer(node)) chain.push(node);
  }
  chain.push(root);
  return chain;
};

/**
 * Part of the screen where the field can be seen: the visible area, clipped by
 * the field's scroll containers and — for app-shell pages, not portal popups —
 * by the visible Header and BottomNav.
 */
const getBand = (field: HTMLElement, chain: HTMLElement[]): VerticalBand => {
  const band = getVisibleArea();
  const root = document.scrollingElement;
  chain.forEach((scroller) => {
    if (scroller === root) return;
    const rect = scroller.getBoundingClientRect();
    band.top = Math.max(band.top, rect.top);
    band.bottom = Math.min(band.bottom, rect.bottom);
  });
  return field.closest('.app') ? clipToAppChrome(band) : band;
};

/** Scroll delta (positive = content moves up) that centres (or pins) the field in the band. */
const getFocusDelta = (field: HTMLElement, rect: DOMRect, band: VerticalBand): number => {
  if (isPinnedField(field)) return rect.top - (band.top + PIN_GAP);
  const height = band.bottom - band.top;
  if (rect.height > height - 2 * CLEARANCE) return rect.top - (band.top + CLEARANCE);
  return (rect.top + rect.bottom) / 2 - (band.top + height * CENTER_RATIO);
};

/** Scroll delta that only brings the field fully into the band (0 when it is). */
const getRevealDelta = (rect: DOMRect, band: VerticalBand): number => {
  if (rect.top < band.top || rect.height > band.bottom - band.top) return rect.top - band.top;
  if (rect.bottom > band.bottom) return rect.bottom - band.bottom;
  return 0;
};

/** Apply the delta to the innermost container first, passing on what it cannot absorb. */
const scrollChainBy = (chain: HTMLElement[], delta: number) => {
  let remaining = delta;
  for (const scroller of chain) {
    if (Math.abs(remaining) < 1) return;
    const before = scroller.scrollTop;
    scroller.scrollTop = before + remaining;
    remaining -= scroller.scrollTop - before;
  }
};

interface UseTelegramKeyboardScrollFixOptions {
  enabled?: boolean;
}

export const useTelegramKeyboardScrollFix = (options: UseTelegramKeyboardScrollFixOptions = {}) => {
  const { enabled = isTelegramMobile() } = options;

  useEffect(() => {
    if (!enabled) return undefined;

    const tg = getTelegramWebApp();
    let field: HTMLInputElement | HTMLTextAreaElement | null = null;
    // Field and band height of the last centring (re-centre only when either changes).
    let centredField: HTMLElement | null = null;
    let centredBandHeight = 0;
    let spacerHost: HTMLElement | null = null;
    let spacerHeight = 0;
    // overflow:auto containers without a height bound (they grow, the document scrolls).
    const growsWithContent = new WeakSet<HTMLElement>();
    let settleTimer = 0;
    let panTimer = 0;
    let closeTimer = 0;

    const clearSpacer = () => {
      if (!spacerHost) return;
      spacerHost.classList.remove(SPACER_CLASS);
      spacerHost.style.removeProperty(SPACER_VAR);
      spacerHost = null;
      spacerHeight = 0;
    };

    const updateSpacer = (chain: HTMLElement[], visibleBottom: number) => {
      const doc = document.scrollingElement as HTMLElement;
      const scroller = chain.find((el) => el === doc || !growsWithContent.has(el)) ?? doc;
      const isDocument = scroller === doc;
      const host = isDocument ? document.body : scroller;
      if (host !== spacerHost) clearSpacer();
      const hostBottom = isDocument ? window.innerHeight : scroller.getBoundingClientRect().bottom - spacerHeight;
      const hidden = Math.max(0, Math.round(hostBottom - visibleBottom));
      if (hidden === spacerHeight) return;
      if (hidden === 0) {
        clearSpacer();
        return;
      }
      const isNewHost = host !== spacerHost;
      const heightBefore = host.clientHeight;
      host.style.setProperty(SPACER_VAR, `${hidden}px`);
      host.classList.add(SPACER_CLASS);
      spacerHost = host;
      spacerHeight = hidden;
      // The spacer must add scroll room, not height: a container that grew
      // has no height bound — leave the scroll room to the next one out.
      if (!isDocument && isNewHost && host.clientHeight > heightBefore) {
        clearSpacer();
        growsWithContent.add(host);
        updateSpacer(chain, visibleBottom);
      }
    };

    // iOS pans the document up by the keyboard height when a field gets focus.
    // A viewport-tall document (every tabbed page — its fields live in inner
    // scroll containers) has nothing to reveal that way: the pan only exposes
    // the empty space below the app, so undo it.
    const undoDocumentPan = () => {
      const doc = document.scrollingElement as HTMLElement;
      if (window.scrollY !== 0 && doc.scrollHeight <= window.innerHeight + 1) {
        window.scrollTo(0, 0);
      }
    };

    const settle = () => {
      if (!field) return;
      // The field can be unmounted (form closed, route changed) without a
      // focusout on iOS — leave once the viewport settles.
      if (!field.isConnected || document.activeElement !== field) {
        if (!isEditableInput(document.activeElement)) close();
        return;
      }
      undoDocumentPan();
      if (field.closest(CHROME_TOP)) {
        clearSpacer();
        return;
      }
      const chain = getScrollChain(field);
      updateSpacer(chain, getVisibleArea().bottom);
      const band = getBand(field, chain);
      const bandHeight = band.bottom - band.top;
      const rect = field.getBoundingClientRect();
      const recentre = field !== centredField || Math.abs(bandHeight - centredBandHeight) > BAND_CHANGE;
      const delta = recentre ? getFocusDelta(field, rect, band) : getRevealDelta(rect, band);
      if (recentre) {
        centredField = field;
        centredBandHeight = bandHeight;
      }
      if (Math.abs(delta) >= MIN_SCROLL) scrollChainBy(chain, delta);
    };

    const scheduleSettle = (delay: number) => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(settle, delay);
    };

    const close = () => {
      window.clearTimeout(settleTimer);
      field = null;
      centredField = null;
      setFieldFocused(false);
      clearSpacer();
      undoDocumentPan();
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (!isEditableInput(e.target as Element)) return;
      // Field → field with the keyboard already up: no viewport events follow.
      const keyboardUp = field !== null;
      window.clearTimeout(closeTimer);
      field = e.target as HTMLInputElement | HTMLTextAreaElement;
      setFieldFocused(true);
      scheduleSettle(keyboardUp ? SETTLE_DELAY : FALLBACK_DELAY);
    };

    const handleFocusOut = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (!isEditableInput(document.activeElement)) close();
      }, CLOSE_DELAY);
    };

    const handleViewportChange = () => {
      if (field) scheduleSettle(SETTLE_DELAY);
    };

    // A pan while the user scrolls or types only gets undone — re-aligning
    // the field here would fight the user's own scrolling.
    const handleViewportScroll = () => {
      if (!field) return;
      window.clearTimeout(panTimer);
      panTimer = window.setTimeout(undoDocumentPan, SETTLE_DELAY);
    };

    const vv = window.visualViewport;
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    window.addEventListener('resize', handleViewportChange);
    vv?.addEventListener('resize', handleViewportChange);
    vv?.addEventListener('scroll', handleViewportScroll);
    tg?.onEvent('viewportChanged', handleViewportChange);

    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
      window.removeEventListener('resize', handleViewportChange);
      vv?.removeEventListener('resize', handleViewportChange);
      vv?.removeEventListener('scroll', handleViewportScroll);
      tg?.offEvent('viewportChanged', handleViewportChange);
      window.clearTimeout(closeTimer);
      window.clearTimeout(panTimer);
      close();
    };
  }, [enabled]);

  return { isEnabled: enabled };
};

export default useTelegramKeyboardScrollFix;
